import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrayHelpers, connect, FieldArray, FormikProps } from 'formik';
import { Button, Divider, Grid, Typography } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';

import { ClientUrlParams, ParamsStatus } from 'api';
import { CustomSVG, Panel } from 'components';
import { useNotify } from 'hooks';

import Names from './Names';
import TrackingKey from './TrackingKey';
import CreateTrackingKey from './CreateTrackingKey';
import DeleteContentDomain from './DeleteContentDomain';

interface IProps {
  param: ClientUrlParams;
}

interface IPropsEnhanced extends IProps, WithStyles<typeof styles> {
  formik: FormikProps<ClientUrlParams>;
}

const styles = (theme: Theme) =>
  createStyles({
    dividerHeader: {
      margin: '12px -16px 24px -16px',
    },
    divider: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
    },
    dividerLast: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(connect, withStyles(styles));

const Parameter: FC<IPropsEnhanced> = ({ classes, formik }) => {
  const { t } = useTranslation();
  const notify = useNotify();

  const { values } = formik;
  const { configuration, content_domain } = values;
  const isEnabled = values.params_status === ParamsStatus.Active;

  function getDividerClassName(index: number) {
    return index === configuration.length - 1 ? classes.dividerLast : classes.divider;
  }

  function handleDelete(arrayHelpers: ArrayHelpers, index: number) {
    if (configuration.length === 1 && isEnabled) {
      notify.error({ message: t('parameters:trackingKeyEnabledError') });
      return;
    }

    arrayHelpers.remove(index);
  }

  function renderToggle(editing: boolean) {
    return editing ? <DeleteContentDomain contentDomain={formik.values.content_domain} /> : null;
  }

  function renderEditBody() {
    return (
      <Fragment>
        <Divider className={classes.dividerHeader} />
        <FieldArray name="configuration">
          {arrayHelpers => (
            <Fragment>
              <CreateTrackingKey onAdd={config => arrayHelpers.insert(0, config)} />
              <Divider className={classes.divider} />
              {configuration.map((config, index) => (
                <Fragment key={index}>
                  <TrackingKey index={index} onDelete={() => handleDelete(arrayHelpers, index)} />
                  <Divider className={getDividerClassName(index)} />
                </Fragment>
              ))}
            </Fragment>
          )}
        </FieldArray>
      </Fragment>
    );
  }

  function renderViewBody() {
    if (!configuration.length) {
      return null;
    }

    return (
      <Fragment>
        <Divider className={classes.dividerHeader} />
        <Grid container spacing={3}>
          {configuration.map((item, index) => (
            <Names key={index} item={item} />
          ))}
        </Grid>
      </Fragment>
    );
  }

  function renderHeader(editing: boolean) {
    return (
      <Grid container alignItems="flex-start" spacing={2}>
        <Grid item>
          <CustomSVG name="track" color={editing ? 'primary' : 'disabled'} />
        </Grid>
        <Grid item>
          <Typography variant="subtitle2">{content_domain}</Typography>
        </Grid>
      </Grid>
    );
  }

  function renderBody(editing: boolean) {
    return editing ? renderEditBody() : renderViewBody();
  }

  function renderControls(editing: boolean, setEditing: (editing: boolean) => void) {
    return (
      <Grid container justify="flex-end" spacing={2}>
        <Grid xs={2} item>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              formik.resetForm();
              setEditing(false);
            }}>
            {t('admin:doneEditing')}
          </Button>
        </Grid>
        <Grid xs={2} item>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => {
              formik.submitForm();
              setEditing(false);
            }}>
            {t('common:save')}
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Panel
      header={renderHeader}
      body={renderBody}
      toggle={renderToggle}
      controls={renderControls}
    />
  );
};

export default enhancers(Parameter);
